// Shared admin UI kit — cards, stats, inline-SVG charts, badges, scaffolds.
// No chart library; everything is inline SVG, matching the learner app.

export const T = {
  ink: '#1e1e2e',
  slate: 'var(--brand-slate)',
  amber: 'var(--brand-amber)',
  emerald: 'var(--brand-emerald)',
  coral: 'var(--brand-coral)',
  purple: '#7c3aed',
  sidebar: '#1a1a24',
  pageBg: '#f6f7f9',
  border: '#e5e7eb',
  surfaceDark: 'var(--surface-dark)',
};

export const card = {
  background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14,
  boxShadow: 'var(--card-shadow)', padding: '18px 20px',
};
export const eyebrow = { fontSize: 10, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: T.slate, margin: 0 };
export const sectionTitle = { fontSize: 15, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: -0.2 };

export function PageHeader({ title, subtitle, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '0 0 5px', letterSpacing: -0.4 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 14, color: T.slate, margin: 0 }}>{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function StatCard({ label, value, delta, deltaColor, icon, tint }) {
  return (
    <div style={{ ...card, flex: '1 1 180px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <p style={eyebrow}>{label}</p>
        {icon && (
          <span style={{ width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: tint || T.amber, background: wash(tint || T.amber) }}>{icon}</span>
        )}
      </div>
      <p style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '8px 0 0', letterSpacing: -0.5 }}>{value}</p>
      {delta != null && <p style={{ fontSize: 12, fontWeight: 700, color: deltaColor || T.slate, margin: '3px 0 0' }}>{delta}</p>}
    </div>
  );
}

export function Pill({ text, color, bg }) {
  return <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color, background: bg, padding: '3px 9px', borderRadius: 99, whiteSpace: 'nowrap' }}>{text}</span>;
}

export function subscriptionBadge(status, tier) {
  if (status === 'active') return <Pill text="Active" color={T.emerald} bg="rgba(5,150,105,0.12)" />;
  if (status === 'cancelled') return <Pill text="Cancelled" color={T.coral} bg="rgba(220,38,38,0.10)" />;
  if (status === 'trialing') return <Pill text="Trial" color={T.amber} bg="rgba(217,119,6,0.12)" />;
  return <Pill text={tier === 'free' ? 'Free' : (status || tier || 'Free')} color={T.slate} bg="#f1f3f7" />;
}

export function Scaffold({ title, body }) {
  return (
    <div style={{ ...card, borderStyle: 'dashed', background: '#fbfbfc' }}>
      <p style={{ ...eyebrow, color: T.slate, display: 'flex', alignItems: 'center', gap: 7 }}>
        <span aria-hidden>🔒</span> Pending data
      </p>
      <p style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, margin: '8px 0 4px' }}>{title}</p>
      <p style={{ fontSize: 12.5, color: T.slate, margin: 0, lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

// ── Inline SVG charts ────────────────────────────────────────────
export function LineChart({ points, color = T.emerald, w = 520, h = 120 }) {
  const vals = points.map(p => p.count ?? p);
  if (vals.length < 2) return <Empty h={h} />;
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0), range = max - min || 1;
  const step = w / (vals.length - 1);
  const xy = vals.map((v, i) => [i * step, h - 10 - ((v - min) / range) * (h - 24)]);
  const d = xy.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${d} L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
      <path d={area} fill={wash(color)} stroke="none" />
      <path d={d} fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      {xy.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="2.6" fill={color} />)}
    </svg>
  );
}

export function BarRow({ rows, color = T.amber, max }) {
  const m = max || Math.max(...rows.map(r => r.n), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: '0 0 130px', fontSize: 12, color: T.slate, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.key}</span>
          <div style={{ flex: 1, height: 9, background: '#eef0f4', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(r.n / m) * 100}%`, background: r.color || color, borderRadius: 99 }} />
          </div>
          <span style={{ flex: '0 0 30px', textAlign: 'right', fontSize: 12, fontWeight: 800, color: T.ink }}>{r.n}</span>
        </div>
      ))}
      {rows.length === 0 && <Empty h={40} />}
    </div>
  );
}

export function Donut({ slices, size = 130 }) {
  const total = slices.reduce((s, x) => s + x.n, 0) || 1;
  const r = size / 2 - 10, c = 2 * Math.PI * r, cx = size / 2;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#eef0f4" strokeWidth="14" />
      {slices.map((s, i) => {
        const frac = s.n / total, len = frac * c;
        const el = <circle key={i} cx={cx} cy={cx} r={r} fill="none" stroke={s.color} strokeWidth="14"
          strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} transform={`rotate(-90 ${cx} ${cx})`} />;
        offset += len; return el;
      })}
      <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 20, fontWeight: 800, fill: T.ink }}>{total}</text>
      <text x="50%" y="62%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, fontWeight: 700, fill: T.slate }}>TOTAL</text>
    </svg>
  );
}

function Empty({ h }) {
  return <div style={{ height: h, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.slate, fontSize: 12 }}>Not enough data yet</div>;
}

export function wash(color) {
  if (color === T.emerald) return 'rgba(5,150,105,0.10)';
  if (color === T.amber) return 'rgba(217,119,6,0.10)';
  if (color === T.coral) return 'rgba(220,38,38,0.09)';
  if (color === T.purple) return 'rgba(124,58,237,0.10)';
  return 'rgba(71,85,105,0.10)';
}
